import {
  ActivatePlanInput,
  ActivatePlanResponse,
  CreateExerciseDto,
  CreatePlanDto,
  CreatePlanWithScheduleDto,
  CreateTreatmentPlanDto,
  EstadoPlan,
  Exercise,
  PlanWithScheduleResult,
  PreviewScheduleResponse,
  ReorderExercisesDto,
  TreatmentPlan,
  TreatmentPlanCreatedResponse,
  UpdateExerciseDto,
  UpdatePlanDto,
} from 'types/models';
import apiClient from './apiClient';

interface PaginatedPlans {
  data: TreatmentPlan[];
  meta: { total: number; page: number; limit: number; pages: number };
}

const base = (patientId: string, episodeId: string) =>
  `/patients/${patientId}/episodes/${episodeId}/plans`;

/** Backend serializa el campo como `status` (prop name) mapeado a DB column `estado`.
 *  Normaliza a `estado` para que todos los componentes existentes funcionen sin cambios. */
const normalizePlan = (raw: any): TreatmentPlan => ({
  ...raw,
  estado: raw.estado ?? raw.status ?? EstadoPlan.BORRADOR,
});

interface GlobalPlanQuery {
  page?: number;
  limit?: number;
  estado?: string;
  profesionalId?: string;
  search?: string;
}

export const planService = {
  listAll: async (params?: GlobalPlanQuery): Promise<PaginatedPlans> => {
    const { data } = await apiClient.get<PaginatedPlans>('/plans', { params: { page: 1, limit: 50, ...params } });
    return data;
  },

  listByEpisode: async (patientId: string, episodeId: string, page = 1, limit = 50): Promise<PaginatedPlans> => {
    const { data } = await apiClient.get<PaginatedPlans>(base(patientId, episodeId), { params: { page, limit } });
    return { ...data, data: data.data.map(normalizePlan) };
  },

  getById: async (patientId: string, episodeId: string, planId: string): Promise<TreatmentPlan> => {
    const { data } = await apiClient.get<TreatmentPlan>(`${base(patientId, episodeId)}/${planId}`);
    return normalizePlan(data);
  },

  create: async (patientId: string, episodeId: string, payload: CreatePlanDto): Promise<TreatmentPlan> => {
    const { data } = await apiClient.post<TreatmentPlan>(base(patientId, episodeId), payload);
    return normalizePlan(data);
  },

  update: async (patientId: string, episodeId: string, planId: string, payload: UpdatePlanDto): Promise<TreatmentPlan> => {
    const { data } = await apiClient.patch<TreatmentPlan>(`${base(patientId, episodeId)}/${planId}`, payload);
    return normalizePlan(data);
  },

  createExercise: async (patientId: string, episodeId: string, planId: string, payload: CreateExerciseDto): Promise<Exercise> => {
    const { data } = await apiClient.post<Exercise>(`${base(patientId, episodeId)}/${planId}/exercises`, payload);
    return data;
  },

  updateExercise: async (patientId: string, episodeId: string, planId: string, exId: string, payload: UpdateExerciseDto): Promise<Exercise> => {
    const { data } = await apiClient.patch<Exercise>(`${base(patientId, episodeId)}/${planId}/exercises/${exId}`, payload);
    return data;
  },

  deleteExercise: async (patientId: string, episodeId: string, planId: string, exId: string): Promise<void> => {
    await apiClient.delete(`${base(patientId, episodeId)}/${planId}/exercises/${exId}`);
  },

  reorderExercises: async (patientId: string, episodeId: string, planId: string, payload: ReorderExercisesDto): Promise<Exercise[]> => {
    const { data } = await apiClient.patch<Exercise[]>(`${base(patientId, episodeId)}/${planId}/exercises/reorder`, payload);
    return data;
  },

  createTreatmentPlan: async (payload: CreateTreatmentPlanDto): Promise<TreatmentPlanCreatedResponse> => {
    const { data } = await apiClient.post<TreatmentPlanCreatedResponse>('/treatment-plans', payload);
    return data;
  },

  previewSchedule: async (planId: string, payload: ActivatePlanInput): Promise<PreviewScheduleResponse> => {
    const { data } = await apiClient.post<PreviewScheduleResponse>(`/treatment-plans/${planId}/validate-schedule`, payload);
    return data;
  },

  activatePlan: async (planId: string, payload: ActivatePlanInput): Promise<ActivatePlanResponse> => {
    const { data } = await apiClient.post<ActivatePlanResponse>(`/treatment-plans/${planId}/activate`, payload);
    return data;
  },

  createWithSchedule: async (
    patientId: string,
    episodeId: string,
    dto: CreatePlanWithScheduleDto,
  ): Promise<PlanWithScheduleResult> => {
    const { data } = await apiClient.post<PlanWithScheduleResult>(
      `/patients/${patientId}/episodes/${episodeId}/plans/with-schedule`,
      dto,
    );
    return data;
  },
};
